import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageSquare, ShoppingCart } from "lucide-react";
import ZiggsLogo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const links = [
    { to: "/", label: "Início", id: "nav-link-inicio" },
    { to: "/catalogo", label: "Catálogo", id: "nav-link-catalogo" },
    { to: "/contato", label: "Contato", id: "nav-link-contato" },
    { to: "/perfil", label: "Conta", id: "nav-link-conta" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const { count } = useCart();

    const linkClass = ({ isActive }) =>
        `relative font-mono text-xs md:text-sm uppercase tracking-[0.2em] transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-forge-yellow after:transition-transform after:duration-300 hover:text-forge-paper hover:after:scale-x-100 ${
            isActive ? "text-forge-yellow after:scale-x-100" : "text-forge-dim"
        }`;

    const contaLabel = (
        <span className="flex items-center gap-2">
            {user?.picture ? (
                <img
                    src={user.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-6 w-6 rounded-full border border-forge-yellow/70 object-cover"
                />
            ) : null}
            Conta
        </span>
    );

    const cartButton = (testid) => (
        <Link
            to="/carrinho"
            data-testid={testid}
            aria-label="Carrinho"
            className="relative inline-flex h-10 w-10 items-center justify-center border border-forge-line text-forge-paper transition-colors duration-300 hover:border-forge-yellow hover:text-forge-yellow"
        >
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && (
                <span
                    data-testid={`${testid}-count`}
                    className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center bg-forge-yellow px-1 font-mono text-[10px] font-bold text-forge-ink"
                >
                    {count}
                </span>
            )}
        </Link>
    );

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-md">
            <nav className="zf-container flex h-16 items-center justify-between md:h-20">
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className="zf-logo group flex items-center gap-3"
                    aria-label="Ziggs Forge — início"
                >
                    <ZiggsLogo className="h-10 w-10 md:h-12 md:w-12" />
                    <span className="font-display text-sm md:text-base font-bold uppercase tracking-tight text-forge-paper">
                        Ziggs<span className="text-forge-yellow">Forge</span>
                    </span>
                </Link>

                <div className="hidden items-center gap-6 md:flex">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={l.id} className={linkClass}>
                            {l.to === "/perfil" ? contaLabel : l.label}
                        </NavLink>
                    ))}
                    {cartButton("nav-cart-button")}
                    <Link
                        to="/contato"
                        data-testid="nav-cta-button"
                        className="inline-flex items-center gap-2 border border-forge-yellow px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-forge-yellow transition-colors duration-300 hover:bg-forge-yellow hover:text-forge-ink"
                    >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Pedir orçamento
                    </Link>
                </div>

                <button
                    className="text-forge-paper md:hidden"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    aria-label="Abrir menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>

            {open && (
                <div className="border-b border-white/10 bg-black/95 px-5 pb-6 pt-2 md:hidden">
                    <div className="flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.id}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `font-mono text-sm uppercase tracking-[0.2em] ${isActive ? "text-forge-yellow" : "text-forge-dim"}`
                                }
                            >
                                {l.to === "/perfil" ? contaLabel : l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/carrinho"
                            data-testid="nav-mobile-cart"
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.2em] text-forge-dim"
                        >
                            <ShoppingCart className="h-4 w-4" />
                            Carrinho {count > 0 ? `(${count})` : ""}
                        </Link>
                        <Link
                            to="/contato"
                            data-testid="nav-mobile-cta"
                            onClick={() => setOpen(false)}
                            className="mt-2 inline-flex items-center justify-center gap-2 border border-forge-yellow px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-forge-yellow"
                        >
                            Pedir orçamento
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
